import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Sun, Tag, PackageOpen, Smile, ArrowRight, Star, Globe, Truck, Store } from "lucide-react";
import { api } from "@/lib/api";
import { BRAND, QUICK_FACTS } from "@/data/brand";
import { GorillaGuarantee } from "@/components/GorillaGuarantee";
import { Reviews } from "@/components/Reviews";
import heroNew from "@/assets/brand/hero-johnnymart.jpg";

const ICONS = { Sun, Tag, PackageOpen, Smile };

const CAT_IMAGES = {
  "Handbags": "https://images.pexels.com/photos/18046813/pexels-photo-18046813.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Electronics": "https://images.pexels.com/photos/12249082/pexels-photo-12249082.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Collectibles & Antiques": "https://images.pexels.com/photos/30133567/pexels-photo-30133567.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Toys": "https://images.unsplash.com/photo-1545558014-8692077e9b5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  "Glassware & Home Decor": "https://images.pexels.com/photos/8794954/pexels-photo-8794954.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Apparel": "https://images.pexels.com/photos/5418892/pexels-photo-5418892.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Health & Beauty": "https://images.unsplash.com/photo-1609451043305-745f798b51a3?auto=format&fit=crop&w=940&q=80",
  "Miscellaneous Treasures": "https://images.pexels.com/photos/30404112/pexels-photo-30404112.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Exclusives": "https://images.unsplash.com/photo-1760804876166-aae5861ec7c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
};

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [mustHaves, setMustHaves] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/shop-affiliates/must-haves").then((r) => setMustHaves(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Helmet>
        <title>JohnnyMart | Online Resale Shop in Ponte Vedra Beach, Florida</title>
        <meta name="description" content="Shop JohnnyMart for clothing, handbags, collectibles, electronics, glassware, toys, books, household finds, and unique merchandise. Shipping and local pickup available." />
        <link rel="canonical" href="https://johnnymart.net/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Store",
          name: "JohnnyMart",
          description: "Online resale shop in Ponte Vedra Beach, Florida offering clothing, handbags, collectibles, electronics, glassware, toys, household finds and unique items.",
          url: "https://johnnymart.net/",
          email: "ShopJohnnyMart@yahoo.com",
          areaServed: "Ponte Vedra Beach, Jacksonville, St. Augustine, FL",
          address: { "@type": "PostalAddress", addressLocality: "Ponte Vedra Beach", addressRegion: "FL", addressCountry: "US" },
        })}</script>
      </Helmet>
      {/* Hero */}
      <section
        className="relative bg-navy overflow-hidden"
      >
        <div className="jm-container py-8 lg:py-12 jm-fade-up">
          <h1 className="sr-only">
            JohnnyMart — Great finds. Cool stuff. Good vibes. Designer bags, collectibles, electronics,
            glassware, clothing, toys and one-of-a-kind treasures in Ponte Vedra Beach, FL.
          </h1>
          <Link to="/shop" data-testid="hero-image-link" className="block group">
            <img
              src={heroNew}
              alt="JohnnyMart — Your Place for Happiness. Great finds, cool stuff, good vibes."
              className="w-full rounded-2xl shadow-2xl border border-cream/10 transition-transform duration-500 group-hover:scale-[1.01]"
            />
          </Link>
          <div className="flex flex-wrap gap-3 mt-7 justify-center">
            <Link
              to="/shop"
              data-testid="hero-browse-btn"
              className="inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-7 py-3.5 hover:bg-gold hover:text-navy transition-colors"
            >
              Shop All <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/policies/pickup"
              data-testid="hero-pickup-btn"
              className="inline-flex items-center gap-2 border-2 border-cream text-cream font-bold uppercase rounded-md px-7 py-3.5 hover:bg-cream hover:text-navy transition-colors"
            >
              <Truck className="w-4 h-4" /> Shipping &amp; Pickup
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-2 border-2 border-cream text-cream font-bold uppercase rounded-md px-7 py-3.5 hover:bg-cream hover:text-navy transition-colors"
            >
              Contact
            </Link>
          </div>
          <p data-testid="home-description" className="mt-6 max-w-3xl mx-auto text-center text-cream/80 leading-relaxed">
            JohnnyMart is a Ponte Vedra Beach online treasure shop offering clothing, handbags, collectibles,
            electronics, glassware, toys, household finds, and other unique items at fair prices. Shipping and
            local pickup options are available on eligible items.
          </p>
        </div>
      </section>

      {/* Welcome + Quick facts */}
      <section className="bg-[#fff8ec]">
        <div className="jm-container py-16 grid gap-10 lg:grid-cols-5 items-center">
          <div className="lg:col-span-2">
            <p className="uppercase tracking-[0.2em] text-brick font-bold text-sm">Welcome to JohnnyMart</p>
            <h2 className="font-display text-4xl sm:text-5xl text-navy mt-3 leading-none">{BRAND.tagline}</h2>
            <p className="mt-4 text-ink/80 leading-relaxed">{BRAND.about}</p>
          </div>
          <div className="lg:col-span-3 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {QUICK_FACTS.map((f) => {
              const Icon = ICONS[f.icon];
              return (
                <article key={f.title} className="bg-white rounded-lg border border-navy/10 p-5 text-center">
                  <Icon className="w-8 h-8 text-brick mx-auto" />
                  <b className="block mt-3 text-navy">{f.title}</b>
                  <span className="block text-sm text-ink/60 mt-1">{f.text}</span>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="jm-container py-16">
        <p className="uppercase tracking-[0.2em] text-brick font-bold text-sm">Shop by category</p>
        <h2 className="font-display text-4xl sm:text-5xl text-navy mt-2">Find your next treasure</h2>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
          {categories.map((c) => {
            const img = CAT_IMAGES[c];
            return (
              <Link
                key={c}
                to={`/shop?category=${encodeURIComponent(c)}`}
                data-testid={`category-tile-${c}`}
                className="group relative h-44 sm:h-52 rounded-lg overflow-hidden border border-navy/15"
              >
                {img && (
                  <img
                    src={img}
                    alt={c}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/30 to-transparent" />
                <span className="absolute bottom-4 left-4 right-4 font-display text-xl text-cream leading-tight">
                  {c}
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Three main shopping areas — always visible on mobile (no hamburger needed) */}
      <section data-testid="home-shop-areas" className="jm-container py-14">
        <div className="text-center">
          <p className="uppercase tracking-[0.2em] text-brick font-bold text-sm">Where would you like to shop?</p>
          <h2 className="font-display text-3xl sm:text-4xl text-navy mt-2">Three ways to shop JohnnyMart</h2>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Shop JohnnyMart */}
          <div data-testid="home-card-shop" className="flex flex-col rounded-xl border border-navy/15 bg-white p-6">
            <div className="inline-flex w-11 h-11 items-center justify-center rounded-lg bg-brick/10 text-brick"><Store className="w-6 h-6" /></div>
            <h3 className="mt-4 font-display text-2xl text-navy">Shop JohnnyMart</h3>
            <p className="mt-2 text-ink/70 leading-relaxed flex-1">Items Johnny personally owns, lists and ships. Buy securely with JohnnyMart checkout.</p>
            <Link to="/shop" data-testid="home-shop-cta" className="mt-5 inline-flex items-center justify-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-5 py-3.5 hover:bg-navy transition-colors">
              Shop JohnnyMart <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {/* Gorilla Gateway (affiliate) */}
          <div data-testid="home-card-gateway" className="flex flex-col rounded-xl border-2 border-gold/60 bg-navy p-6">
            <div className="inline-flex w-11 h-11 items-center justify-center rounded-lg bg-gold/15 text-gold"><Globe className="w-6 h-6" /></div>
            <h3 className="mt-4 font-display text-2xl text-cream">Enter the Gorilla Gateway</h3>
            <p className="mt-2 text-cream/75 leading-relaxed flex-1">Shop trusted retailers like Amazon through JohnnyMart's affiliate links — including Johnny's Must Haves. You buy directly from the retailer at its normal price.</p>
            <Link to="/gateway" data-testid="home-gateway-cta" className="mt-5 inline-flex items-center justify-center gap-2 bg-gold text-navy font-bold uppercase rounded-md px-5 py-3.5 hover:bg-cream transition-colors">
              Enter the Gorilla Gateway <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {/* Other Partners (dropship) */}
          <div data-testid="home-card-partners" className="flex flex-col rounded-xl border border-navy/15 bg-white p-6">
            <div className="inline-flex w-11 h-11 items-center justify-center rounded-lg bg-navy/10 text-navy"><PackageOpen className="w-6 h-6" /></div>
            <h3 className="mt-4 font-display text-2xl text-navy">Other Partners</h3>
            <p className="mt-2 text-ink/70 leading-relaxed flex-1">Products supplied and shipped by JohnnyMart's outside product partners, delivered to your door.</p>
            <Link to="/other-partners" data-testid="home-partners-cta" className="mt-5 inline-flex items-center justify-center gap-2 bg-navy text-cream font-bold uppercase rounded-md px-5 py-3.5 hover:bg-brick transition-colors">
              Shop Other Partners <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Johnny's Must Haves preview */}
        {mustHaves.length > 0 && (
          <div data-testid="home-must-haves" className="mt-10">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-display text-2xl text-navy">Johnny's Must Haves</h3>
                <p className="text-ink/70 text-sm">Products Johnny owns, uses, or personally recommends.</p>
              </div>
              <Link to="/gateway" data-testid="see-all-must-haves" className="text-sm font-bold text-brick hover:text-navy">See all Must Haves →</Link>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {mustHaves.map((m) => (
                <div key={m.id} data-testid={`home-musthave-${m.id}`} className="rounded-xl border border-navy/15 bg-white overflow-hidden flex flex-col">
                  <div className="aspect-[4/3] bg-cream flex items-center justify-center border-b border-navy/10">
                    {m.amazon_image_link ? <img src={m.amazon_image_link} alt={m.title} loading="lazy" className="max-w-full max-h-full object-contain" /> : m.image_url ? <img src={m.image_url} alt={m.title} className="w-full h-full object-cover" /> : <span className="text-navy/40 text-xs uppercase font-bold">View on Amazon</span>}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h4 className="font-bold text-navy text-sm leading-tight">{m.title}</h4>
                    {m.recommendation && <p className="mt-1 text-xs text-ink/70 flex-1">"{m.recommendation}"</p>}
                    <Link to="/gateway" className="mt-3 text-xs font-bold text-brick uppercase">View in Gateway →</Link>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-ink/50">As an Amazon Associate, JohnnyMart earns from qualifying purchases.</p>
          </div>
        )}
      </section>

      <Reviews />

      <GorillaGuarantee />

      {/* Contact CTA */}
      <section className="bg-[#fff8ec]">
        <div className="jm-container py-16 text-center">
          <Star className="w-10 h-10 text-gold mx-auto" />
          <h2 className="font-display text-4xl sm:text-5xl text-navy mt-4">See something you like?</h2>
          <p className="max-w-2xl mx-auto mt-4 text-ink/75 leading-relaxed">
            Message JohnnyMart to ask whether an item is still available, request more pictures, or arrange
            pickup or shipping.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/contact" className="bg-brick text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy transition-colors">
              Contact JohnnyMart
            </Link>
            <Link to="/shop" className="border-2 border-navy text-navy font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy hover:text-cream transition-colors">
              Keep Shopping
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
